import { computed, defineComponent, nextTick, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { withLoading } from '@hems/component';
import { UserReportService } from '@hems/service';
import { divideArray, getEnergyBalance, getParam, getDateInitialValue, getNowByClient, } from '@hems/user/src/containers/report/chartFunctions';
import { getConsumtionOptions } from '@hems/user/src/containers/report/consumption/chartOptions';
import { useLocale } from '@hems/util';
import { getTime } from '@hems/util/src/helper/dateHelper';
import { GraphChart, EnergyBalance, CalendarHourly, CalendarDaily, CalendarMonth } from '@/components';
export default defineComponent({
    name: 'ConsumptionContainer',
    components: {
        GraphChart,
        EnergyBalance,
        CalendarHourly,
        CalendarDaily,
        CalendarMonth,
    },
    props: {
        tabname: {
            type: String,
            required: true,
            default: 'hourly',
        },
    },
    setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const state = reactive({
            tabname: props.tabname,
            showChart: false,
            data: {
                coreLoadData: [],
                consumtionData: [],
            },
            energyBalance: [],
            date: null,
            siteConfigurationInfo: store.state.device.siteConfiguration ?? null,
        });
        const defaultBaseDeviceInfo = {
            device_id: '',
            site_id: 0,
            timezone_id: '',
            product_model_nm: '',
        };
        const { intlFormatLocale } = useLocale();
        const timezoneId = computed(() => store.state.appCtx.timeZone ?? '');
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const isACSystem = computed(() => store.state.device.gen_type?.startsWith('AC_SYS') ?? false);
        const service = new UserReportService(window.axiosInstance.axios);
        const options = computed(() => getConsumtionOptions(state.tabname, intlFormatLocale.value, state.data, t, isACSystem.value));
        async function getReportsByInterval(tabname) {
            if (state.date == null)
                return;
            const date = state.date;
            await withLoading(async () => {
                await service
                    .getReportsByInterval(tabname, getParam(date, 'con', tabname, baseDeviceInfo.value ?? defaultBaseDeviceInfo))
                    .then(({ result }) => {
                    state.energyBalance = isACSystem.value
                        ? divideArray([{ value: result.con_kwh.origin || 0, unit: 'kWh', type: 'device.consumed' }], 2)
                        : divideArray([
                            { value: result.con_kwh.origin || 0, unit: 'kWh', type: 'device.consumed' },
                            { value: result.core_kwh.origin || 0, unit: 'kWh', type: 'device.core_load' },
                        ], 2);
                    state.data.consumtionData = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.con_data[index] || 0,
                    }));
                    if (!isACSystem.value) {
                        state.data.coreLoadData = result.colec_time.map((TIME, index) => ({
                            x: getTime(TIME, tabname),
                            y: result.core_data[index] || 0,
                        }));
                    }
                })
                    .catch((e) => {
                    console.error(e);
                    state.energyBalance = [[], [], [], [], []];
                    state.data.consumtionData = [];
                    state.data.coreLoadData = [];
                });
            })();
            nextTick(() => (state.showChart = true));
        }
        function getServerTime() {
            service
                .getServerTime(baseDeviceInfo.value?.timezone_id ?? '')
                .then(async (res) => {
                state.date = getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = getDateInitialValue(getNowByClient(timezoneId.value));
            });
        }
        function rerenderDataGrid() {
            state.showChart = false;
            nextTick(() => (state.showChart = true));
        }
        watch(() => props.tabname, (tabname) => {
            state.tabname = tabname;
        });
        watch(() => state.tabname, () => {
            state.showChart = false;
            getReportsByInterval(state.tabname);
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            getReportsByInterval(state.tabname);
        }, {
            deep: true,
        });
        watch(() => intlFormatLocale.value, () => {
            rerenderDataGrid();
        });
        getServerTime();
        return {
            state,
            options,
            energyBalance: computed(() => getEnergyBalance(state.energyBalance, intlFormatLocale.value, t)),
        };
    },
});
